import { useEffect, useMemo, useRef, useState } from "react";

import {
  AlertTriangle,
  Check,
  ChevronsUpDown,
  Loader2,
  Plus,
  Trash2,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { useRecordMermas } from "~/hooks/production";
import {
  getStockAt,
  useOilTypeStockByPlant,
  useOilTypes,
  useRawMaterials,
  useRawMaterialStockByPlant,
} from "~/hooks/purchasing";
import { useToast } from "~/hooks/use-toast";
import { OIL_UNITS, UNIT_LABELS, type Plant } from "~/types/purchasing";
import { formatCurrency } from "~/utils/helpers";

import type { StockByPlantMap } from "~/hooks/purchasing";
import type { MermaLineInput, ProductionBatch } from "~/types/production";

interface MermaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  batch?: ProductionBatch; // present → merma tied to this lote
}

interface IngredientOption {
  key: string;
  kind: "oil" | "raw_material";
  id: string;
  name: string;
  unit: string;
  avgCostClp: number | null; // per-liter for oils, per-unit for materials
  density: number | null;
}

// One editable row in the dialog. Plant + reason are shared across all rows.
interface LineState {
  rowId: number;
  ingredientKey: string;
  quantity: string;
  oilUnit: string;
}

// Values derived from a row's inputs (cost, stock, validity) for display + submit.
interface LineDerived {
  option: IngredientOption | null;
  qtyNum: number;
  isOil: boolean;
  entryUnit: string;
  submitUnit: string;
  estimatedCost: number | null;
  availableStock: number | null;
  insufficient: boolean;
  valid: boolean;
}

const PLANTS = [
  { value: "P1", label: "Planta 1" },
  { value: "P2", label: "Planta 2" },
];

function deriveLine(
  option: IngredientOption | null,
  quantity: string,
  oilUnit: string,
  plant: string,
  oilStockByPlant: StockByPlantMap | undefined,
  rawStockByPlant: StockByPlantMap | undefined
): LineDerived {
  const qtyNum = parseFloat(quantity) || 0;
  const isOil = option?.kind === "oil";
  const entryUnit = isOil
    ? (OIL_UNITS.find((u) => u.value === oilUnit)?.label ?? oilUnit)
    : option
      ? (UNIT_LABELS[option.unit] ?? option.unit)
      : "";
  const submitUnit = isOil ? oilUnit : (option?.unit ?? "");

  // Entered quantity converted to liters (oils) for costing + stock comparison.
  let litersEquivalent = qtyNum;
  if (option && isOil) {
    const density = option.density ?? 0.92;
    if (oilUnit === "kg") litersEquivalent = qtyNum / density;
    else if (oilUnit === "tonnes") litersEquivalent = (qtyNum * 1000) / density;
    else litersEquivalent = qtyNum;
  }

  const estimatedCost =
    option?.avgCostClp != null
      ? isOil
        ? litersEquivalent * option.avgCostClp
        : qtyNum * option.avgCostClp
      : null;

  let availableStock: number | null = null;
  if (option && plant) {
    if (isOil) {
      const liters = getStockAt(oilStockByPlant, option.id, plant as Plant);
      availableStock = option.density != null ? liters * option.density : liters;
    } else {
      availableStock = getStockAt(rawStockByPlant, option.id, plant as Plant);
    }
  }

  const enteredInKg = isOil
    ? litersEquivalent * (option?.density ?? 0.92)
    : qtyNum;
  const insufficient =
    availableStock != null && qtyNum > 0 && enteredInKg > availableStock;

  return {
    option,
    qtyNum,
    isOil,
    entryUnit,
    submitUnit,
    estimatedCost,
    availableStock,
    insufficient,
    valid: !!option && qtyNum > 0,
  };
}

export function MermaDialog({
  open,
  onOpenChange,
  onSuccess,
  batch,
}: MermaDialogProps) {
  const { toast } = useToast();
  const record = useRecordMermas();
  const { data: oilTypes = [] } = useOilTypes();
  const { data: rawMaterials = [] } = useRawMaterials();
  const { data: oilStockByPlant } = useOilTypeStockByPlant();
  const { data: rawStockByPlant } = useRawMaterialStockByPlant();

  const rowIdRef = useRef(0);
  const makeLine = (): LineState => ({
    rowId: rowIdRef.current++,
    ingredientKey: "",
    quantity: "",
    oilUnit: "kg",
  });

  const [lines, setLines] = useState<LineState[]>(() => [makeLine()]);
  const [plant, setPlant] = useState("");
  const [reason, setReason] = useState("");
  const [openPickerRow, setOpenPickerRow] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setLines([makeLine()]);
      setPlant(batch?.plant ?? "");
      setReason("");
      setOpenPickerRow(null);
      setConfirmOpen(false);
    }
  }, [open, batch?.plant]);

  // Options: the lote's ingredients when batch-tied, else the full catalog.
  const options = useMemo<IngredientOption[]>(() => {
    if (batch) {
      const seen = new Set<string>();
      const list: IngredientOption[] = [];
      for (const ing of batch.ingredients ?? []) {
        if (
          ing.oil_type_id &&
          ing.oil_type &&
          !seen.has(`oil-${ing.oil_type_id}`)
        ) {
          seen.add(`oil-${ing.oil_type_id}`);
          list.push({
            key: `oil-${ing.oil_type_id}`,
            kind: "oil",
            id: ing.oil_type_id,
            name: ing.oil_type.code
              ? `${ing.oil_type.code} - ${ing.oil_type.name}`
              : ing.oil_type.name,
            unit: "kg",
            avgCostClp: ing.oil_type.average_cost_clp ?? null,
            density: ing.oil_type.density_kg_per_liter ?? null,
          });
        } else if (
          ing.raw_material_id &&
          ing.raw_material &&
          !seen.has(`rm-${ing.raw_material_id}`)
        ) {
          seen.add(`rm-${ing.raw_material_id}`);
          list.push({
            key: `rm-${ing.raw_material_id}`,
            kind: "raw_material",
            id: ing.raw_material_id,
            name: ing.raw_material.code
              ? `${ing.raw_material.code} - ${ing.raw_material.name}`
              : ing.raw_material.name,
            unit: ing.raw_material.unit,
            avgCostClp: ing.raw_material.average_cost_clp ?? null,
            density: null,
          });
        }
      }
      return list;
    }
    return [
      ...oilTypes.map((o) => ({
        key: `oil-${o.id}`,
        kind: "oil" as const,
        id: o.id,
        name: o.code ? `${o.code} - ${o.name}` : o.name,
        unit: "kg",
        avgCostClp: o.average_cost_clp ?? null,
        density: o.density_kg_per_liter ?? null,
      })),
      ...rawMaterials.map((r) => ({
        key: `rm-${r.id}`,
        kind: "raw_material" as const,
        id: r.id,
        name: r.code ? `${r.code} - ${r.name}` : r.name,
        unit: r.unit,
        avgCostClp: r.average_cost_clp ?? null,
        density: null,
      })),
    ];
  }, [batch, oilTypes, rawMaterials]);

  const derivedLines = useMemo<LineDerived[]>(
    () =>
      lines.map((ln) =>
        deriveLine(
          options.find((o) => o.key === ln.ingredientKey) ?? null,
          ln.quantity,
          ln.oilUnit,
          plant,
          oilStockByPlant,
          rawStockByPlant
        )
      ),
    [lines, options, plant, oilStockByPlant, rawStockByPlant]
  );

  const validDerived = derivedLines.filter((d) => d.valid);
  const validCount = validDerived.length;
  const knownCosts = validDerived
    .map((d) => d.estimatedCost)
    .filter((c): c is number => c != null);
  const totalCost = knownCosts.length
    ? knownCosts.reduce((a, b) => a + b, 0)
    : null;

  const updateLine = (index: number, patch: Partial<LineState>) =>
    setLines((prev) =>
      prev.map((ln, i) => (i === index ? { ...ln, ...patch } : ln))
    );
  const addLine = () => setLines((prev) => [...prev, makeLine()]);
  const removeLine = (index: number) =>
    setLines((prev) =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== index)
    );

  const canSubmit =
    validCount > 0 &&
    !!plant &&
    reason.trim().length > 0 &&
    !record.isPending;

  const handleConfirm = () => {
    if (!plant || validCount === 0) return;
    const payloadLines: MermaLineInput[] = derivedLines
      .filter((d) => d.valid && d.option)
      .map((d) => ({
        ingredient_kind: d.option!.kind,
        oil_type_id: d.option!.kind === "oil" ? d.option!.id : null,
        raw_material_id:
          d.option!.kind === "raw_material" ? d.option!.id : null,
        quantity: d.qtyNum,
        unit: d.submitUnit,
      }));

    record.mutate(
      {
        plant,
        reason: reason.trim(),
        batch_id: batch?.id ?? null,
        lines: payloadLines,
      },
      {
        onSuccess: (res) => {
          toast({
            title:
              res.count === 1 ? "Merma registrada" : "Mermas registradas",
            description: `${res.count} ${
              res.count === 1 ? "línea" : "líneas"
            } · $${formatCurrency(
              res.total_cost_clp
            )} (no afecta el costo del PT).`,
          });
          setConfirmOpen(false);
          onOpenChange(false);
          onSuccess();
        },
        onError: (error) => {
          setConfirmOpen(false);
          toast({
            title: "No se pudo registrar",
            description: (error as Error).message,
            variant: "destructive",
          });
        },
      }
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Registrar merma{batch ? ` — ${batch.batch_number}` : ""}
            </DialogTitle>
            <DialogDescription>
              Pérdida de materia prima o aceite. Se descuenta del stock y su costo
              se registra como merma (no afecta el costo del PT).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2 sm:max-w-[220px]">
              <Label>Planta</Label>
              <Select value={plant} onValueChange={setPlant}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Planta" />
                </SelectTrigger>
                <SelectContent>
                  {PLANTS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Ingredientes</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addLine}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Agregar merma
                </Button>
              </div>

              <div className="space-y-3">
                {lines.map((ln, index) => {
                  const d = derivedLines[index];
                  return (
                    <div
                      key={ln.rowId}
                      className="space-y-2 rounded-lg border p-3 animate-in fade-in-0 slide-in-from-top-1 duration-200"
                    >
                      <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                          {batch ? (
                            <Select
                              value={ln.ingredientKey}
                              onValueChange={(value) =>
                                updateLine(index, { ingredientKey: value })
                              }
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Selecciona un ingrediente del lote" />
                              </SelectTrigger>
                              <SelectContent>
                                {options.map((o) => (
                                  <SelectItem key={o.key} value={o.key}>
                                    <span className="flex items-center gap-2">
                                      <Badge
                                        variant={
                                          o.kind === "oil"
                                            ? "default"
                                            : "secondary"
                                        }
                                        className="text-xs"
                                      >
                                        {o.kind === "oil"
                                          ? "Aceite"
                                          : "Material"}
                                      </Badge>
                                      {o.name}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Popover
                              open={openPickerRow === ln.rowId}
                              onOpenChange={(o) =>
                                setOpenPickerRow(o ? ln.rowId : null)
                              }
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  className="h-9 w-full justify-between font-normal"
                                >
                                  <span className="truncate">
                                    {d.option
                                      ? d.option.name
                                      : "Buscar ingrediente…"}
                                  </span>
                                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                className="w-[420px] p-0"
                                align="start"
                                style={{ pointerEvents: "auto" }}
                                onOpenAutoFocus={(e) => e.preventDefault()}
                              >
                                <Command style={{ pointerEvents: "auto" }}>
                                  <CommandInput placeholder="Buscar aceite o materia prima…" />
                                  <CommandList>
                                    <CommandEmpty>Sin resultados.</CommandEmpty>
                                    <CommandGroup heading="Aceites">
                                      {options
                                        .filter((o) => o.kind === "oil")
                                        .map((o) => (
                                          <CommandItem
                                            key={o.key}
                                            value={o.name}
                                            onSelect={() => {
                                              updateLine(index, {
                                                ingredientKey: o.key,
                                              });
                                              setOpenPickerRow(null);
                                            }}
                                            className="cursor-pointer"
                                            style={{ pointerEvents: "auto" }}
                                          >
                                            <Check
                                              className={`mr-2 h-4 w-4 ${
                                                ln.ingredientKey === o.key
                                                  ? "opacity-100"
                                                  : "opacity-0"
                                              }`}
                                            />
                                            <span className="flex-1 truncate">
                                              {o.name}
                                            </span>
                                          </CommandItem>
                                        ))}
                                    </CommandGroup>
                                    <CommandGroup heading="Materias primas">
                                      {options
                                        .filter((o) => o.kind === "raw_material")
                                        .map((o) => (
                                          <CommandItem
                                            key={o.key}
                                            value={o.name}
                                            onSelect={() => {
                                              updateLine(index, {
                                                ingredientKey: o.key,
                                              });
                                              setOpenPickerRow(null);
                                            }}
                                            className="cursor-pointer"
                                            style={{ pointerEvents: "auto" }}
                                          >
                                            <Check
                                              className={`mr-2 h-4 w-4 ${
                                                ln.ingredientKey === o.key
                                                  ? "opacity-100"
                                                  : "opacity-0"
                                              }`}
                                            />
                                            <span className="flex-1 truncate">
                                              {o.name}
                                            </span>
                                          </CommandItem>
                                        ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          )}
                        </div>

                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 shrink-0"
                          onClick={() => removeLine(index)}
                          disabled={lines.length === 1}
                          aria-label="Quitar línea"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>

                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          step="any"
                          min="0"
                          value={ln.quantity}
                          onChange={(e) =>
                            updateLine(index, { quantity: e.target.value })
                          }
                          placeholder="0"
                          className="h-9 w-28 shrink-0"
                          aria-label="Cantidad"
                        />

                        {d.isOil ? (
                          <Select
                            value={ln.oilUnit}
                            onValueChange={(value) =>
                              updateLine(index, { oilUnit: value })
                            }
                          >
                            <SelectTrigger className="h-9 w-[140px] shrink-0">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {OIL_UNITS.filter((u) =>
                                ["kg", "liters", "tonnes"].includes(u.value)
                              ).map((u) => (
                                <SelectItem key={u.value} value={u.value}>
                                  {u.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="flex h-9 shrink-0 items-center px-1 text-sm text-muted-foreground">
                            {d.entryUnit || "—"}
                          </span>
                        )}
                      </div>

                      {d.option && (
                        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs">
                          <span className="text-muted-foreground">
                            {d.estimatedCost != null
                              ? `Costo: $${formatCurrency(d.estimatedCost)}`
                              : "Sin costo en ficha"}
                            {d.availableStock != null
                              ? ` · Stock ${plant}: ${d.availableStock.toFixed(
                                  2
                                )} ${d.isOil ? "kg" : d.entryUnit}`
                              : ""}
                          </span>
                          {d.insufficient && (
                            <span className="flex items-center gap-1 font-medium text-red-700">
                              <AlertTriangle className="h-3.5 w-3.5" />
                              Supera el stock — quedará negativo
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {validCount > 0 && (
              <div className="rounded-lg border bg-muted/50 p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    Costo total de la merma
                  </span>
                  <span className="font-semibold tabular-nums text-red-700">
                    {totalCost != null ? (
                      `$${formatCurrency(totalCost)}`
                    ) : (
                      <span className="text-muted-foreground">
                        Sin costo en ficha
                      </span>
                    )}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Se registra como merma — no afecta el costo del PT.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="merma-reason">Motivo</Label>
              <Textarea
                id="merma-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Motivo de la merma (derrame, contaminación, etc.)"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button disabled={!canSubmit} onClick={() => setConfirmOpen(true)}>
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar merma</AlertDialogTitle>
            <AlertDialogDescription>
              Se registrarán {validCount}{" "}
              {validCount === 1 ? "merma" : "mermas"} en {plant} como merma (no
              afecta el costo del PT). ¿Confirmar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={record.isPending}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
              disabled={record.isPending}
            >
              {record.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
